// Phase NAATI-MAX B3 — /translate/naati/doctype/$slug (TH) per-doctype hub
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_DOCTYPES, type NaatiDoctype } from "@/data/naati-doctypes";
import HubLeadCTA from "@/components/HubLeadCTA";
import { NAATI_CATEGORIES } from "@/data/naati-categories";
import { NAATI_COUNTRIES } from "@/data/naati-countries";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/naati/doctype/$slug")({
  loader: ({ params }) => {
    const doc = NAATI_DOCTYPES.find((d) => d.slug === params.slug);
    if (!doc) throw notFound();
    const parent = NAATI_CATEGORIES.find((c) => c.doctypeSlugs.includes(doc.slug));
    const siblings = parent
      ? parent.doctypeSlugs
          .map((s) => NAATI_DOCTYPES.find((d) => d.slug === s))
          .filter((d): d is NaatiDoctype => Boolean(d) && d!.slug !== doc.slug)
      : [];
    return { doc, parent, siblings };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const { doc } = loaderData;
    const url = `${SITE}/translate/naati/doctype/${params.slug}`;
    const enUrl = `/en/translate/naati/doctype/${params.slug}`;
    const title = `แปล ${doc.nameTh} รับรอง NAATI — ราคา ฿${doc.priceFromThb.toLocaleString()}/หน้า | NYC`;
    const desc = `บริการแปล ${doc.nameTh} (${doc.nameEn}) รับรอง NAATI ตราดิจิทัล QR Code อัปโหลด ImmiAccount ได้ทันที — ระยะเวลา ${doc.turnaround}. ${doc.notesTh.slice(0, 80)}. ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/doctype/$slug", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati/doctype/$slug", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/doctype/$slug", "th")}, { property: "og:url", content: url },
        { property: "og:description", content: ctaDesc(desc, "/translate/naati/doctype/$slug", "th")}, { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang(`/translate/naati/doctype/${params.slug}`, enUrl),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "Service",
          "@id": `${url}#service`, name: title, description: desc, inLanguage: "th",
          serviceType: "NAATI Certified Translation",
          provider: { "@type": "ProfessionalService", name: "NYC Language Institute", url: SITE },
          areaServed: { "@type": "Country", name: "Australia" },
          offers: { "@type": "Offer", price: doc.priceFromThb, priceCurrency: "THB",
            availability: "https://schema.org/InStock",
            priceSpecification: { "@type": "UnitPriceSpecification", price: doc.priceFromThb, priceCurrency: "THB", unitText: "ต่อหน้า A4" } },
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: `แปล ${doc.nameTh} รับรอง NAATI ราคาเท่าไหร่?`,
              acceptedAnswer: { "@type": "Answer", text: `ราคาเริ่มต้น ฿${doc.priceFromThb.toLocaleString()} ต่อหน้า A4 รวมตรา NAATI Digital Stamp พร้อม QR Code แล้ว ระยะเวลา ${doc.turnaround} ส่งไฟล์ PDF ทางอีเมล อัปโหลด ImmiAccount ได้ทันที.`}},
            { "@type": "Question", name: `ต้องเตรียมเอกสารอย่างไร?`,
              acceptedAnswer: { "@type": "Answer", text: doc.notesTh }},
            { "@type": "Question", name: `ใช้กับวีซ่าออสเตรเลียประเภทใดได้บ้าง?`,
              acceptedAnswer: { "@type": "Answer", text: `ใช้กับ ${doc.useCases.join(", ")} และวีซ่าออสเตรเลียอื่นๆ ที่ DHA กำหนด.`}},
            { "@type": "Question", name: `ตรา NAATI Digital Stamp ปลอมแปลงได้หรือไม่?`,
              acceptedAnswer: { "@type": "Answer", text: `ไม่ได้ — ตราดิจิทัล NAATI มี QR Code ที่เชื่อมโยงกับฐานข้อมูล NAATI โดยตรง สามารถสแกนตรวจสอบความถูกต้องของนักแปลและงานแปลได้ทันที (CPN ต่ออายุทุก 3 ปี).`}},
          ],
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "HowTo",
          name: `วิธีสั่งแปล ${doc.nameTh} รับรอง NAATI`,
          totalTime: `PT${doc.turnaround.match(/\d+/)?.[0] ?? "3"}D`,
          estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: doc.priceFromThb },
          step: [
            { "@type": "HowToStep", position: 1, name: "ส่งเอกสาร", text: `สแกนหรือถ่ายรูป ${doc.nameTh} ความละเอียด 300dpi ขึ้นไป ส่งทาง LINE หรืออีเมล` },
            { "@type": "HowToStep", position: 2, name: "ยืนยันราคา", text: `ทีมตรวจเอกสาร 30 นาที ส่งใบเสนอราคา + เลขที่งาน` },
            { "@type": "HowToStep", position: 3, name: "ชำระเงิน", text: `โอนผ่านธนาคารหรือบัตรเครดิต` },
            { "@type": "HowToStep", position: 4, name: "รับงาน", text: `ภายใน ${doc.turnaround} ส่ง PDF + QR ตราดิจิทัล NAATI พร้อมอัปโหลด ImmiAccount` },
          ],
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "NAATI", item: `${SITE}/translate/naati` },
            { "@type": "ListItem", position: 3, name: doc.nameTh, item: url },
          ],
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#speakable`, url,
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
        })},
      ],
    };
  },
  component: DoctypePage,
});

function DoctypePage() {
  const { doc, parent, siblings } = Route.useLoaderData() as {
    doc: NaatiDoctype;
    parent: typeof NAATI_CATEGORIES[number] | undefined;
    siblings: NaatiDoctype[];
  };
  return (
    <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
      <nav className="mb-6 text-xs text-slate-500">
        <Link to="/" className="hover:text-amber-700">หน้าแรก</Link> /{" "}
        <Link to="/translate/naati" className="hover:text-amber-700">NAATI</Link> /{" "}
        <span>{doc.nameTh}</span>
      </nav>
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">NAATI Certified · {doc.category.toUpperCase()}</div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">
        แปล {doc.nameTh} รับรอง NAATI
      </h1>
      <p className="mt-3 text-lg text-slate-700 direct-answer" data-speakable="true">
        ราคาเริ่มต้น <strong>฿{doc.priceFromThb.toLocaleString()}/หน้า</strong> รวมตรา NAATI Digital Stamp ที่มี QR Code
        ระยะเวลา <strong>{doc.turnaround}</strong> ส่งไฟล์ PDF ทางอีเมล อัปโหลด ImmiAccount ได้ทันที
        ไม่ต้องส่งไปรษณีย์ — จัดรูปแบบคำแปลตามข้อกำหนดของ DHA
      </p>

      <section className="mt-10 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา</div>
          <div className="mt-1 text-2xl font-extrabold text-navy-deep">฿{doc.priceFromThb.toLocaleString()}</div>
          <div className="text-xs text-slate-500">ต่อหน้า A4 (รวมตรา NAATI)</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ระยะเวลา</div>
          <div className="mt-1 text-2xl font-extrabold text-navy-deep">{doc.turnaround}</div>
          <div className="text-xs text-slate-500">ด่วน +50% ภายใน 24 ชม.</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ความยาวทั่วไป</div>
          <div className="mt-1 text-2xl font-extrabold text-navy-deep">{doc.pagesTypical}</div>
          <div className="text-xs text-slate-500">คำนวณราคาตามจำนวนหน้าจริง</div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">ข้อกำหนดและสิ่งที่ต้องเตรียม</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">{doc.notesTh}</p>
        <p className="mt-2 text-sm text-slate-500 italic">EN: {doc.notesEn}</p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">วีซ่า/หน่วยงานออสเตรเลียที่ใช้เอกสารนี้</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          {doc.useCases.map((u, i) => (
            <li key={i} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">{u}</li>
          ))}
        </ul>
      </section>

      <section className="mt-10 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">ขั้นตอนการสั่งงาน 4 ขั้นตอน</h2>
        <ol className="mt-3 space-y-2 text-sm text-slate-700">
          <li><strong>1) ส่งเอกสาร:</strong> สแกนหรือถ่ายรูป 300dpi ส่ง LINE @nyclanguage หรืออีเมล</li>
          <li><strong>2) ยืนยันราคา:</strong> ทีมตรวจ 30 นาที ส่งใบเสนอราคา + เลขที่งาน</li>
          <li><strong>3) ชำระเงิน:</strong> โอนผ่านธนาคารหรือบัตรเครดิต (รับใบเสร็จภาษีได้)</li>
          <li><strong>4) รับงาน:</strong> ภายใน {doc.turnaround} ส่ง PDF + QR Code ตรา NAATI พร้อมอัปโหลด ImmiAccount</li>
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">FAQ</h2>
        <div className="mt-4 space-y-4">
          <details className="rounded-xl border border-slate-200 bg-white p-4" open>
            <summary className="cursor-pointer font-semibold text-navy-deep">ตรา NAATI Digital Stamp ปลอมแปลงได้หรือไม่?</summary>
            <p className="mt-2 text-sm text-slate-700">ไม่ได้ — ตราดิจิทัล NAATI มี QR Code เชื่อมโยงฐานข้อมูล NAATI โดยตรง สแกนตรวจสอบนักแปลและงานแปลได้ทันที (CPN ต่ออายุทุก 3 ปี).</p>
          </details>
          <details className="rounded-xl border border-slate-200 bg-white p-4">
            <summary className="cursor-pointer font-semibold text-navy-deep">DHA จะตีกลับงานแปลของเราหรือไม่?</summary>
            <p className="mt-2 text-sm text-slate-700">ไม่ตีกลับ — นักแปลของเราถือ NAATI CPN ปัจจุบัน ทำงานตามมาตรฐาน NAATI Code of Ethics. หากถูกร้องขอแก้ไข เราแก้ฟรีตลอดอายุการใช้งาน.</p>
          </details>
          <details className="rounded-xl border border-slate-200 bg-white p-4">
            <summary className="cursor-pointer font-semibold text-navy-deep">รับงานด่วนภายใน 24 ชั่วโมงได้หรือไม่?</summary>
            <p className="mt-2 text-sm text-slate-700">ได้ — ค่าด่วน +50% จากราคามาตรฐาน. ส่งเอกสารก่อน 11:00 น. รับงานวันถัดไปก่อน 18:00 น.</p>
          </details>
        </div>
      </section>

      {siblings.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">เอกสารกลุ่มเดียวกัน{parent ? ` (${parent.nameTh})` : ""}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {siblings.slice(0, 9).map((s) => (
              <Link key={s.slug} to="/translate/naati/doctype/$slug" params={{ slug: s.slug }}
                className="rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-400">
                <div className="font-semibold text-navy-deep">{s.nameTh}</div>
                <div className="text-xs text-slate-500">฿{s.priceFromThb.toLocaleString()} · {s.turnaround}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">แปล {doc.nameTh} สำหรับยื่นในประเทศ</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {NAATI_COUNTRIES.slice(0, 12).map((c) => (
            <Link key={c.slug} to="/translate/naati/online/$country" params={{ country: c.slug }}
              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs hover:border-amber-400">
              {c.nameTh}
            </Link>
          ))}
        </div>
      </section>
      <HubLeadCTA locale="th" service="NAATI Document Type" />
      <QuotableStatsStrip cluster="naati" locale="th" variant="light" />
    </main>
  );
}
